import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { guestGuard } from './guards/guest.guard';
import { associatedPatientsGuard } from './guards/associated-patients.guard';
import { nonQuickActivatedGuard } from './guards/non-quick-activated.guard';
import { quickActivatedGuard } from './guards/quick-activated.guard';
import { tenantConfigurationResolver } from './resolver/tenant-configuration.resolver';
import { forgotPasswordLinkResolver } from './resolver/forgot-password-link.resolver';
import { mfaPendingGuard } from './guards/mfa-pending.guard';
import { bookAppointmentGuard } from './guards/book-appointment.guard';

/**
 * Wraps a lazy-loaded component with error handling for chunk loading failures.
 * When a chunk loading error occurs (e.g., after a new deployment), this will
 * reload the page to fetch the updated chunks.
 */
function loadComponentWithErrorHandling<T>(
  importFn: () => Promise<T>
): () => Promise<T> {
  return () => {
    return importFn().catch((error: any) => {
      // Check if this is a chunk loading error
      const errorMessage = error?.message || error?.toString() || '';
      const isChunkError = 
        errorMessage.includes('Failed to fetch dynamically imported module') ||
        errorMessage.includes('Loading chunk') ||
        errorMessage.includes('ChunkLoadError') ||
        errorMessage.includes('Failed to load chunk') ||
        errorMessage.includes('Importing a module script failed');

      if (isChunkError) {
        console.warn('Chunk loading error detected. Reloading page to fetch updated chunks...', error);
        // Simply reload the page - this resolves the problem
        window.location.reload();
        // Return a promise that never resolves (page will reload)
        return new Promise(() => {});
      }
      // Re-throw non-chunk errors
      throw error;
    });
  };
}

export const routes: Routes = [
  {
    path: '',
    redirectTo: '**',
    pathMatch: 'full',
  },
  {
    path: 'home',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/home/home.component').then(c => c.HomeComponent)
    ),
    canActivate: [authGuard]
  },
  {
    path: 'associatedPatients',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/associated-patients/associated-patients.component').then(c => c.AssociatedPatientsComponent)
    ),
    canActivate: [authGuard, associatedPatientsGuard]
  },
  {
    path: 'audit-log',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/audit-log/audit-log.component').then(c => c.AuditLogComponent)
    ),
    canActivate: [authGuard]
  },
  {
    path: 'restriction',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/restriction-request/restriction-request.component').then(c => c.RestrictionRequestComponent)
    ),
    canActivate: [authGuard]
  },
  {
    path: 'messages',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/messages/messages.component').then(c => c.MessagesComponent)
    ),
    canActivate: [authGuard]
  },
  {
    path: 'patientMsg',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/patient-message/patient-message.component').then(c => c.PatientMessageComponent)
    ),
    canActivate: [authGuard]
  },
  {
    path: 'ask-provider',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/ask-provider/ask-provider.component').then(c => c.AskProviderComponent)
    ),
    canActivate: [authGuard]
  },
  {
    path: 'profile',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/user-profile/user-profile.component').then(c => c.UserProfileComponent)
    ),
    canActivate: [authGuard]
  },
  {
    path: 'account-settings',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/change-password/change-password.component').then(c => c.ChangePasswordComponent)
    ),
    canActivate: [authGuard, nonQuickActivatedGuard]
  },
  {
    path: 'appointments',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/appointments/appointments.component').then(c => c.AppointmentsComponent)
    ),
    canActivate: [authGuard]
  },
  {
    path: 'book-appointment',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/book-appointment/book-appointment.component').then(c => c.BookAppointmentComponent)
    ),
    canActivate: [authGuard, bookAppointmentGuard]
  },
  {
    path: 'health-record',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/health-record/health-record.component').then(c => c.HealthRecordComponent)
    ),
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'health-information', pathMatch: 'full' },
      {
        path: 'health-information',
        loadComponent: loadComponentWithErrorHandling(
          () => import('./pages/health-information/health-information.component').then(c => c.HealthInformationComponent)
        )
      },
      {
        path: 'visit-summary',
        loadComponent: loadComponentWithErrorHandling(
          () => import('./pages/visit-summary/visit-summary.component').then(c => c.VisitSummaryComponent)
        )
      },
      {
        path: 'exam-notes',
        loadComponent: loadComponentWithErrorHandling(
          () => import('./pages/exam-notes/exam-notes.component').then(c => c.ExamNotesComponent)
        )
      },
      {
        // PE-6130 / PE-6131 / PE-6132 — Labs Results
        path: 'results',
        loadComponent: loadComponentWithErrorHandling(
          () => import('./pages/results/results.component').then(c => c.ResultsComponent)
        )
      }
    ]
  },
  {
    path: 'statements',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/patient-statement/patient-statement.component').then(c => c.PatientStatementComponent)
    ),
    canActivate: [authGuard]
  },
  {
    path: 'patient-forms',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/health-form-list/health-form-list.component').then(c => c.HealthFormListComponent)
    ),
    canActivate: [authGuard]
  },
  {
    path: 'phi/view',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/phi-view/phi-view.component').then(c => c.PhiViewComponent)
    ),
    canActivate: [authGuard]
  },
  {
    path: 'phi/submit',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/phi-submit/phi-submit.component').then(c => c.PhiSubmitComponent)
    ),
    canActivate: [authGuard]
  },
  {
    path: 'quick-activate',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/quick-activate/quick-activate.component').then(c => c.QuickActivateComponent)
    ),
    canActivate: [authGuard, quickActivatedGuard]
  },
  {
    path: 'patient-mfa',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/patient-mfa/patient-mfa.component').then(c => c.PatientMfaComponent)
    ),
    canActivate: [mfaPendingGuard]
  },
  {
    path: 'page-not-found',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/page-not-found/page-not-found.component').then(c => c.PageNotFoundComponent)
    )
  },
  {
    path: ':tenantShortName/PatientActivation.html',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/registration/registration.component').then(c => c.RegistrationComponent)
    ),
    runGuardsAndResolvers: 'paramsChange',
    resolve: { tenantConfig: tenantConfigurationResolver },
    canActivate: [guestGuard],
  },
  // PE-6148 — guest new-patient registration entry (provider website + Register CTA)
  {
    path: ':tenantShortName/sign-up',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/sign-up/sign-up.component').then(c => c.SignUpComponent)
    ),
    runGuardsAndResolvers: 'paramsChange',
    resolve: { tenantConfig: tenantConfigurationResolver },
    canActivate: [guestGuard],
  },
  {
    path: ':tenantShortName/forgot-username',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/forgot-username/forgot-username.component').then(c => c.ForgotUsernameComponent)
    ),
    runGuardsAndResolvers: 'paramsChange',
    resolve: { tenantConfig: tenantConfigurationResolver },
    canActivate: [guestGuard],
  },
  {
    path: ':tenantShortName/request-password-reset',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/request-password-reset/request-password-reset.component').then(c => c.RequestPasswordResetComponent)
    ),
    runGuardsAndResolvers: 'paramsChange',
    resolve: { tenantConfig: tenantConfigurationResolver },
    canActivate: [guestGuard],
  },
  {
    path: ':tenantShortName/forgot-password',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/forgot-password/forgot-password.component').then(c => c.ForgotPasswordComponent)
    ),
    runGuardsAndResolvers: 'paramsChange',
    resolve: { 
      tenantConfig: tenantConfigurationResolver,
      linkData: forgotPasswordLinkResolver
    },
    canActivate: [guestGuard],
  },
  {
    path: ':tenantShortName',
    loadComponent: loadComponentWithErrorHandling(
      () => import('./pages/sign-in/sign-in.component').then(c => c.SignInComponent)
    ),
    runGuardsAndResolvers: 'paramsChange',
    resolve: { tenantConfig: tenantConfigurationResolver },
    canActivate: [guestGuard],
  },
  {
    path: '**',
    redirectTo: 'page-not-found',
    pathMatch: 'full'
  }
];





